import React, {useEffect, useMemo, useState} from 'react';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import AdmicityStatus from '../../../../../shared-components/AdmicityStatus';
import {
    useGetFormsByStudentQuery,
    useGetGeneralFormsQuery,
    useLazyGetGeneralFormResponsesQuery
} from '../../../../../api/services/formRequestsService';
import AdmicityTableV2 from '../../../../../shared-components/Table/V2/AdmicityTableV2';
import {formatDate} from '../../../../../utility/dateUtil';
import withPaginationV2 from '../../../../../hoc/pagination/withPaginationV2';
import {getFormRequestStatusInfo} from '../../../RequestAndResponses/common/common';
import FILTER_OPERATIONS from '../../../../../constants/filterOperations';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../../../constants/formRequestStatuses';
import {
    downloadFormResponsesPdfReport,
    downloadParentFormResponsesPdfReport
} from '../../../../../api/services/filesService';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import useNotification from '../../../../../utility/hooks/useNotification';
import AdmicityTabs from '../../../../../shared-components/AdmicityTabs';
import {Grid, Paper, Typography, useMediaQuery, useTheme} from '@mui/material';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';

/* eslint-disable react/prop-types */
const formsTableColumns = [
    {
        accessorKey: 'form.name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())}/>,
        filterVariant: 'select',
        filterFn: FILTER_OPERATIONS.EQUALS,
        filterSelectOptions: Object.values(FORM_REQUEST_STATUSES)
            .map(statusId => ({
                value: statusId,
                label: FORM_REQUEST_STATUSES_NAMES[statusId]
            })),
    },
    {
        accessorKey: 'expirationDate',
        header: 'Expiration Date',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedBy.fullname',
        header: 'Submitted By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
];

const generalFormsTableColumns = [
    {
        accessorKey: 'name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    }
];

const getFormRequestsColumns = () => [
    {
        header: 'Status',
        accessor: 'status',
    },
    {
        header: 'Submitted Date',
        accessor: 'editedAt',
    },
    {
        header: 'Submitted By',
        accessor: 'editedBy.fullname',
    }
];

/* eslint-enable react/prop-types */
const StudentForms = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    const downloadFile = useFileDownloader();
    const {showSuccessNotification} = useNotification();
    const navigate = useNavigate();
    const {studentId} = useParams();
    const outletContext = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(outletContext.formsState?.currentTabIndex ?? 0);
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(outletContext.formsState?.selectedNotificationFormIndex);
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = useGetGeneralFormsQuery(studentId, {skip: currentTabIndex !== 1});
    const Table = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetFormsByStudentQuery,
        props: {studentId}
    }), [studentId]);

    useEffect(() => {
        return () => {
            outletContext.setFormsState({currentTabIndex, selectedNotificationFormIndex});
        };
    }, [outletContext.setFormsState, currentTabIndex, selectedNotificationFormIndex]);

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (formRequestId && !isBulk) {
            await downloadFile(
                downloadFormResponsesPdfReport,
                {
                    formId,
                    formRequestId
                });
        } else {
            await downloadParentFormResponsesPdfReport(studentId);
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    const tabs =
        [
            {
                label: 'Forms',
                component: <Table
                    variant="elevation"
                    id={outletContext?.key}
                    columns={formsTableColumns}
                    tableProps={{
                        enableSorting: false,
                        enableFullScreenToggle: false
                    }}
                    toolbarIconActions={
                        [{
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }]
                    }
                    onRowClick={(params) => navigate(`${params.id}`)}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No requests and responses yet'
                    }}
                    rowActions={[
                        {
                            label: 'Download PDF',
                            action: params => downloadPdfReport(params.id, params.form.id, false),
                            disabled: row => row.status !== FORM_REQUEST_STATUSES.submitted
                        }
                    ]}
                />
            },
            {
                label: 'Notification Forms',
                component:
                    <AdmicityTableV2
                        variant="elevation"
                        id={`${outletContext?.key}/general`}
                        columns={generalFormsTableColumns}
                        data={notificationForms}
                        isLoading={(isLoadingNotificationForms || isFetchingNotificationForms)}
                        initialState={{
                            expanded: selectedNotificationFormIndex !== undefined ? {[selectedNotificationFormIndex]: true} : {},
                            columnFilters: []
                        }}
                        tableProps={{
                            enableSorting: false,
                            layoutMode: 'grid-no-grow',
                            enableColumnResizing: false,
                            enableRowActions: true,
                            enableBottomToolbar: false,
                            enableTopToolbar: false,
                            manualFiltering: false,
                            enablePagination: false,
                            manualSorting: false,
                            enableHiding: false,
                            showGlobalFilter: true,
                            enableExpandAll: false,
                            muiExpandButtonProps: ({row, table}) => ({
                                onClick: () => {
                                    const isExpanded = row.getIsExpanded();

                                    if (!isExpanded) {
                                        setSelectedNotificationFormIndex(row.index);
                                    }

                                    table.setExpanded({[row.id]: !isExpanded});
                                },
                            }),
                            renderDetailPanel: ({row}) => (
                                <FormRequestsPanel
                                    formId={row.original.formId}
                                    studentId={studentId}
                                    navigate={navigate}
                                />
                            ),
                            muiDetailPanelProps: {
                                sx: {
                                    '& .MuiCollapse-root': {
                                        width: '100%',
                                    }
                                }
                            },
                        }}
                        onRowClick={(params) => navigate(`general/${params.formId}`)}
                        rowActions={[
                            {
                                label: 'Submit',
                                action: (params) => navigate(`general/${params.formId}`)
                            }
                        ]}
                        noRowsOverlay={{
                            icon: <ContentPasteOffRoundedIcon/>,
                            text: 'No forms available'
                        }}
                    />
            },
        ];

    return (
        <Paper
            variant="outlined"
            sx={
                isSmallScreen
                    ? {}
                    : {
                        minHeight: '100%',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
        >
            <AdmicityTabs
                tabs={tabs}
                tabIndex={currentTabIndex}
                onChange={(index) => {
                    setCurrentTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                }}
                displayDivider
            />
        </Paper>

    );
};

const FormRequestsPanel = ({formId, studentId, navigate}) => {
    const [getFormResponses, {data: responses = [], isLoading}] = useLazyGetGeneralFormResponsesQuery();
    const columns = getFormRequestsColumns();

    useEffect(() => {
        getFormResponses({
            formId,
            studentId
        });
    }, [formId, getFormResponses]);

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width='100%'
            >
                <CircularProgress size={20}/>
            </Box>
        );
    }

    if (responses.length === 0) {
        return (
            <Typography
                color="text.secondary"
                width="100%"
                textAlign="center"
            >
                No submissions yet
            </Typography>
        );
    }

    return (
        <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
                cursor: 'default'
            }}
        >
            <Typography
                gutterBottom
                fontWeight={600}>
                Submissions
            </Typography>
            <Paper variant="outlined">
                <Grid
                    container
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        typography: 'subtitle2',
                        paddingLeft: 1,
                        paddingRight: 1,
                        paddingTop: 2,
                        paddingBottom: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        overflow: 'hidden',
                        fontWeight: 600
                    }}
                >
                    {columns.map((column) => (
                        <Grid item xs={4} key={column.header}>
                            {column.header}
                        </Grid>
                    ))}
                </Grid>
                {responses.map((request) => (
                    <Grid
                        container
                        key={request.id}
                        sx={{
                            typography: 'body2',
                            paddingLeft: 1,
                            paddingRight: 1,
                            paddingTop: 2,
                            paddingBottom: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                                border: 'none'
                            },
                            ':hover': {
                                backgroundColor: 'action.hover',
                                cursor: 'pointer',
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${request.id}`);
                        }}
                    >
                        <Grid item xs={4}>
                            <AdmicityStatus {...getFormRequestStatusInfo(request.status)} />
                        </Grid>
                        <Grid item xs={4}>
                            {request.editedAt ? formatDate(request.editedAt) : '-'}
                        </Grid>
                        <Grid item xs={4}>
                            {request.editedBy?.fullname || '-'}
                        </Grid>
                    </Grid>
                ))}
            </Paper>
        </Box>
    );
};

FormRequestsPanel.propTypes = {
    formId: PropTypes.number.isRequired,
    studentId: PropTypes.number.isRequired,
    navigate: PropTypes.func.isRequired,
};

export default StudentForms;